<template>
  <div>
    <!-- modals -->
    <b-modal
      v-model="showSaveModal"
      title="Save this patient data as ..."
      @show="initModal"
      @hidden="initModal"
      @ok="savePatient"
    >
      <form ref="formRef" @submit.stop.prevent="handleSubmit">
        <label>Customize a description or use default</label>
        <b-form-input
          id="name-input"
          v-model="description"
          type="text"
          minlength="5"
          :maxlength="description_max"
          required
        />
        <span class="limiter">{{ charactersLeft }}</span>
        <br />
        <div v-if="storedPatient" class="form-check form-switch col-3">
          <input id="switch" v-model="saveNew" class="form-check-input" type="checkbox" />
          <label for="switch" class="form-check-label">Save a copy</label>
        </div>
      </form>
    </b-modal>
    <b-modal v-model="showClearModal" title="Clear patient data" @ok="clearPatient">
      <div>Are you sure you want to clear all the patient data?</div>
    </b-modal>
    <!-- main content -->
    <b-card class="shadow rounded text-md-start">
      <b-container>
        <b-overlay :show="isLoading" rounded="sm" class="h-50">
          <b-row v-if="model">
            <b-col sm="12" md="3">
              <div class="text-white rounded summary-badge">{{ adult }}</div>
              <div
                v-if="storedPatient"
                class="my-2 p-2 rounded align-content-center text-wrap text-bg-secondary"
              >
                {{ storedPatient.label }}
              </div>
            </b-col>
            <b-col sm="12" md="6">
              <div class="summary-text overflow-auto">
                <ul>
                  <li class="text-lg text-black font-semibold">
                    {{ drug?.name }}
                  </li>
                  <li v-if="model" class="text-muted font-italic">{{ model.name }}</li>
                  <li v-if="patient?.id" class="text-muted font-medium">
                    {{ patient.gender }} {{ patient.age }}{{ displaySIUnits(patient.ageUnit) }}
                    {{ patient.height }}{{ patient.heightUnit }} {{ patient.weight
                    }}{{ patient.weightUnit }} {{ bmi }} BMI
                  </li>
                  <li v-if="pathology" class="text-muted">
                    MIC: {{ pathology.mic }}
                    <span
                      class="text-muted font-medium"
                      v-html="displaySIUnits(pathology.micUnit)"
                    ></span>
                    ({{ pathology.pathogen }})
                  </li>
                  <li v-if="target" class="text-muted font-medium">
                    {{ target.target }} {{ target.pdtMin }}-{{ target.pdtMax }}
                    <span
                      class="text-muted font-medium"
                      v-html="displaySIUnits(target.pdtUnit)"
                    ></span>
                  </li>
                </ul>
              </div>
            </b-col>
            <b-col sm="12" md="3">
              <ul class="actions flex">
                <li v-if="ready">
                  <b-button
                    variant="outline-primary"
                    title="Save this patient data"
                    @click="saveModal"
                    >SAVE
                  </b-button>
                </li>
                <li>
                  <b-button
                    variant="outline-secondary"
                    title="Clear patient data"
                    @click="clearData"
                    >CLEAR
                  </b-button>
                </li>
              </ul>
            </b-col>
          </b-row>
          <b-row v-else>
            <p class="alert alert-info">
              Please select a drug and enter patient data to start your simulation or select a saved
              patient below
            </p>
            <div class="text-left">
              <v-select
                id="gender"
                v-model="storedPatient"
                placeholder="Select a patient"
                class="w-80 text-left"
                :options="patientList"
                :clearable="true"
                @update:model-value="loadData"
              ></v-select>
            </div>
          </b-row>
          <template #overlay>
            <div class="text-center">
              <i class="bi bi-stopwatch" style="font-size: 3em"></i>
              <p id="cancel-label">Please wait whilst simulation is running...</p>
            </div>
          </template>
        </b-overlay>
      </b-container>
    </b-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, toRaw } from 'vue';
import { Patient } from '@/models/Patient';
import { PatientData } from '@/models/PatientData';
import IDB from '@/components/services/vidb';
import {
  createSamplePatient,
  createSamplePatientThom2009,
  createSamplePatientGentamicin,
  createSamplePatientLlop2006,
  createSamplePatientAdan2015,
  createSamplePatientMasi2020,
  createSamplePatientGoti2018,
  createSamplePatientGoti2018Dialysis,
} from '@/components/services/common';
import { displaySIUnits } from '@/models/utils/constants';
import { useDosingStore } from '@/stores/dosing';
import { useToast } from 'vue-toastification';

defineOptions({ name: 'PatientSummary' });

const emit = defineEmits<{
  (e: 'updateSavedList', evt: any, id: string): void;
  (e: 'switchTab', evt: any, tab: number): void;
}>();

const store = useDosingStore();
const toast = useToast();

const showSaveModal = ref(false);
const showClearModal = ref(false);
const description = ref<string | null>(null);
const description_max = 60;
const saveNew = ref(false);
const storedPatient = ref<{ code: string; label: string } | null>(null);
const patientList = ref<{ code: string; label: string }[]>([]);
const formRef = ref<HTMLFormElement | null>(null);

const bmi = computed(() => patient.value.bmi() ?? '');
const drug = computed(() => store.getDrug);
const model = computed(() => store.getModel ?? null);
const patient = computed(() => new Patient(store.getPatient));
const adult = computed(() => model.value?.population);
const delivery = computed(() => store.getDelivery);
const pathology = computed(() => store.getPathology);
const target = computed(() => store.getTarget);
const selected = computed(() => store.getSelectedId);
const ready = computed(() => store.isReady);
const isLoading = computed(() => store.getLoading);

const charactersLeft = computed(() => {
  const char = description.value ? description.value.length : 0;
  return description_max - char + ' / ' + description_max + ' characters remaining';
});

onMounted(async () => {
  const samplePatient = createSamplePatient(null);
  await IDB.set(samplePatient.id, samplePatient);
  const sampleThom = createSamplePatientThom2009(null);
  await IDB.set(sampleThom.id, sampleThom);
  const sampleGent = createSamplePatientGentamicin(null);
  await IDB.set(sampleGent.id, sampleGent);
  const sampleLlop = createSamplePatientLlop2006(null);
  await IDB.set(sampleLlop.id, sampleLlop);
  const sampleAdan = createSamplePatientAdan2015(null);
  await IDB.set(sampleAdan.id, sampleAdan);
  const sampleMasi = createSamplePatientMasi2020(null);
  await IDB.set(sampleMasi.id, sampleMasi);
  const sampleGoti = createSamplePatientGoti2018(null);
  await IDB.set(sampleGoti.id, sampleGoti);
  const sampleGotiDial = createSamplePatientGoti2018Dialysis(null);
  await IDB.set(sampleGotiDial.id, sampleGotiDial);
  getPatientList();
});

async function savePatient($event: any) {
  const patientData = new PatientData();
  if (storedPatient.value && !saveNew.value) {
    const id = storedPatient.value.code;
    await IDB.remove(id);
    patientData.id = id;
  } else {
    patientData.id = crypto.randomUUID().split('-')[0];
    storedPatient.value = { code: patientData.id, label: description.value! };
  }
  patientData.patient = toRaw(patient.value);
  patientData.modelId = model.value.code;
  patientData.description = description.value;
  patientData.delivery = toRaw(delivery.value);
  patientData.pathology = toRaw(pathology.value);
  patientData.target = toRaw(target.value);
  patientData.doses = store.getDoses.map((d) => toRaw(d));
  await IDB.set(patientData.id, patientData);
  await getPatientList();
  emit('updateSavedList', $event, patientData.id);
  toast.success(`Patient data saved: ${patientData.id}`);
}

function initModal() {
  description.value = `${model.value.drug} ${patient.value.gender} ${patient.value.age}y`;
  if (selected.value) {
    IDB.get(selected.value).then((p: any) => {
      const label = `[${p.id}] ${p.description}`;
      storedPatient.value = { code: p.id, label };
      description.value = p.description;
    });
  }
  saveNew.value = false;
}

function handleSubmit($event: Event) {
  $event.preventDefault();
  if (formRef.value?.checkValidity() && description.value && description.value.length > 0) {
    savePatient($event);
  }
}

function saveModal() {
  showSaveModal.value = true;
}
function clearData() {
  showClearModal.value = true;
}

function clearPatient($event: any) {
  emit('updateSavedList', $event, '');
  storedPatient.value = null;
  description.value = null;
  store.clearAll();
  emit('switchTab', $event, 1);
}

function loadData() {
  if (storedPatient.value) {
    IDB.get(storedPatient.value.code).then((p: any) => {
      store.loadPatientData(p);
    });
  } else {
    store.clearAll();
  }
}

async function getPatientList() {
  IDB.getAll()
    .then((p: any[]) => {
      patientList.value = p.map((item) => {
        const label = `[${item.id}] ${item.description}`;
        return { code: item.id, label };
      });
    })
    .catch((e: any) => console.error(e));
}
</script>

<style scoped>
.summary-badge {
  padding: 20px;
  width: 100px;
  background-color: #2c3e50;
}

.summary-text {
  text-align: left;
  padding: 0;
  margin: 0;
  font-size: 0.9em;
}

.summary-text ul {
  list-style: none;
}

.actions {
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  list-style: none;
}

label {
  font-weight: bold;
  float: left;
}

input {
  text-align: left;
}

.limiter {
  font-size: 0.8em;
  color: #2c3e50;
  float: right;
}

.actions {
  flex-direction: column;
}

.actions li {
  margin-right: 3px;
}

@media (max-width: 740px) {
  .actions {
    flex-direction: row;
  }
}
</style>
